import { useState, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "wouter";
import { Navbar } from "@/components/layout/Navbar";
import { USE_CASES, PLAN_META, type UseCaseCategory, type SubCategory, type UseCasePlan } from "@/data/useCases";
import { ArrowRight, ChevronDown, Filter, Zap } from "lucide-react";

// ── Filter config ──────────────────────────────────────────────────────────

const TABS: { id: UseCaseCategory | "All"; label: string }[] = [
  { id: "All",          label: "All" },
  { id: "Free",         label: "Free" },
  { id: "Pro & Pro Plus", label: "Pro & Pro Plus" },
];

const SUB_FILTERS: Partial<Record<UseCaseCategory | "All", string[]>> = {
  "Free":         ["All", "Individual", "Business", "Supply Chain"],
  "Pro & Pro Plus": ["All", "Service Business", "Retail", "Documents"],
};

const PLAN_COLOR: Record<UseCasePlan, { bg: string; text: string; dot: string }> = {
  free:       { bg: "bg-emerald-100", text: "text-emerald-700", dot: "bg-emerald-500" },
  pro:        { bg: "bg-violet-100",  text: "text-violet-700",  dot: "bg-violet-500" },
  pro_plus:   { bg: "bg-blue-100",    text: "text-blue-700",    dot: "bg-blue-500" },
};

const PLAN_LABEL: Record<UseCasePlan, string> = {
  free: "Free", pro: "Pro", pro_plus: "Pro Plus",
};

const USE_CASE_COUNT = USE_CASES.length;
const PLAN_COUNT = Object.keys(PLAN_META).length;

// ── Components ─────────────────────────────────────────────────────────────

function UseCaseCard({ uc }: { uc: (typeof USE_CASES)[0] }) {
  const col = PLAN_COLOR[uc.plan];
  return (
    <Link
      href={`/use-cases/${uc.slug}`}
      className="group block bg-card border border-border/60 rounded-2xl p-5 hover:shadow-lg hover:border-primary/30 hover:-translate-y-0.5 transition-all"
    >
      {/* Icon */}
      <div className="w-12 h-12 rounded-2xl bg-muted flex items-center justify-center text-2xl mb-4 group-hover:scale-110 transition-transform">
        {uc.icon}
      </div>
      {/* Plan badge */}
      <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full mb-3 ${col.bg} ${col.text}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${col.dot}`} />
        {PLAN_LABEL[uc.plan]}
      </span>
      {/* Title */}
      <h3 className="font-bold text-sm leading-snug mb-1.5 group-hover:text-primary transition-colors">
        {uc.title}
      </h3>
      {/* Description */}
      <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2 mb-4">
        {uc.description}
      </p>
      {/* Learn more */}
      <span className="text-xs font-semibold text-primary flex items-center gap-1 group-hover:gap-2 transition-all">
        Learn more <ArrowRight className="w-3.5 h-3.5" />
      </span>
    </Link>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────

export default function UseCasesGallery() {
  const [activeTab, setActiveTab] = useState<UseCaseCategory | "All">("All");
  const [activeSub, setActiveSub] = useState<string>("All");
  const [showAll, setShowAll] = useState(false);

  const subFilters = SUB_FILTERS[activeTab] ?? [];

  const filtered = useMemo(() => {
    let list = USE_CASES;
    if (activeTab !== "All") list = list.filter((u) => u.category === activeTab);
    if (activeSub !== "All") list = list.filter((u) => u.subCategory === activeSub);
    if (!showAll) list = list.filter((u) => u.tier === 1);
    return list;
  }, [activeTab, activeSub, showAll]);

  const totalCount = useMemo(() => {
    let list = USE_CASES;
    if (activeTab !== "All") list = list.filter((u) => u.category === activeTab);
    if (activeSub !== "All") list = list.filter((u) => u.subCategory === activeSub);
    return list.length;
  }, [activeTab, activeSub]);

  function handleTabChange(tab: UseCaseCategory | "All") {
    setActiveTab(tab);
    setActiveSub("All");
    setShowAll(false);
  }

  const tier1Count = filtered.length;
  const hiddenCount = totalCount - (showAll ? 0 : tier1Count);

  return (
    <>
      <Helmet>
        <title>{`Use Cases — ${USE_CASE_COUNT} Ways to Use CommonQR | CommonQR`}</title>
        <meta name="description" content={`Explore ${USE_CASE_COUNT} real-world use cases for CommonQR — from free smart business cards to verified business documents.`} />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Navbar />

        {/* ── Hero ── */}
        <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-primary/80 pt-20 pb-16 text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-10"
            style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "28px 28px" }}
          />
          <div className="relative max-w-4xl mx-auto px-6 text-center">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white text-xs font-bold px-4 py-2 rounded-full mb-6">
              <Zap className="w-3.5 h-3.5" /> {USE_CASE_COUNT} Use Cases · {PLAN_COUNT} Plans
            </div>
            <h1 className="text-5xl sm:text-6xl font-display font-bold leading-tight mb-4">
              Every Industry.<br className="hidden sm:block" /> One Platform.
            </h1>
            <p className="text-lg text-white/70 max-w-2xl mx-auto mb-8">
              From free smart business cards to verified business documents — CommonQR works for every business, at every scale.
            </p>
            {/* Stats */}
            <div className="flex flex-wrap justify-center gap-8 text-center">
              {[
                { n: `${USE_CASE_COUNT}`, label: "Use Cases" },
                { n: `${PLAN_COUNT}`, label: "Plans" },
                { n: "20+", label: "Industries" },
                { n: "50K+", label: "Active Profiles" },
              ].map((s) => (
                <div key={s.label}>
                  <div className="text-3xl font-bold">{s.n}</div>
                  <div className="text-sm text-white/60">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Plan tabs ── */}
        <div className="bg-card border-b border-border/50 sticky top-0 z-20 shadow-sm">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex overflow-x-auto gap-1 py-3 scrollbar-hide">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`whitespace-nowrap px-4 py-2 rounded-xl text-sm font-semibold transition-all shrink-0 ${
                    activeTab === tab.id
                      ? "bg-primary text-primary-foreground shadow-md shadow-primary/20"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Sub-filters */}
            {subFilters.length > 0 && (
              <div className="flex flex-wrap gap-2 pb-3">
                {subFilters.map((sub) => (
                  <button
                    key={sub}
                    onClick={() => { setActiveSub(sub); setShowAll(false); }}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
                      activeSub === sub
                        ? "bg-foreground text-background border-foreground"
                        : "border-border text-muted-foreground hover:border-foreground hover:text-foreground"
                    }`}
                  >
                    {sub}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Card grid ── */}
        <div className="max-w-7xl mx-auto px-6 py-12">

          {/* Results header */}
          <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                Showing <span className="font-bold text-foreground">{filtered.length}</span>
                {!showAll && totalCount > filtered.length ? ` featured` : ""} of{" "}
                <span className="font-semibold text-foreground">{totalCount}</span> use cases
                {activeTab !== "All" ? ` in ${activeTab}` : ""}
                {activeSub !== "All" ? ` · ${activeSub}` : ""}
              </span>
            </div>
            <Link
              href="/signup"
              className="text-sm font-bold text-primary flex items-center gap-1.5 hover:gap-2.5 transition-all"
            >
              Start free — no credit card <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {filtered.length === 0 ? (
            <div className="text-center py-24 text-muted-foreground">
              <p className="text-4xl mb-4">🔍</p>
              <p className="font-semibold">No use cases found for this combination.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {filtered.map((uc) => (
                <UseCaseCard key={uc.slug} uc={uc} />
              ))}
            </div>
          )}

          {/* View all button */}
          {!showAll && hiddenCount > 0 && (
            <div className="mt-12 text-center">
              <div className="inline-flex flex-col items-center gap-3">
                <p className="text-sm text-muted-foreground">
                  + {hiddenCount} more use cases available
                </p>
                <button
                  onClick={() => setShowAll(true)}
                  className="inline-flex items-center gap-2 px-8 py-4 border-2 border-primary text-primary font-bold rounded-xl hover:bg-primary hover:text-primary-foreground transition-all"
                >
                  View all use cases <ChevronDown className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── Plan comparison strip ── */}
        <div className="border-t border-border/50 bg-muted/30 py-16">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-2xl font-display font-bold text-center mb-10">Which plan is right for you?</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {(Object.entries(PLAN_META) as [UseCasePlan, typeof PLAN_META[UseCasePlan]][]).map(([plan, meta]) => {
                const count = USE_CASES.filter((u) => u.plan === plan).length;
                return (
                  <Link
                    key={plan}
                    href="/pricing"
                    className={`group block rounded-2xl border-2 ${meta.border} ${meta.bg} p-5 text-center hover:shadow-md transition-all hover:-translate-y-0.5`}
                  >
                    <div className={`text-xs font-bold uppercase tracking-widest mb-2 ${meta.text}`}>{meta.badge}</div>
                    <div className="text-2xl font-bold mb-1 text-foreground">{count}</div>
                    <div className="text-xs text-muted-foreground mb-3">use cases</div>
                    <div className={`text-xs font-semibold ${meta.text} flex items-center justify-center gap-1 group-hover:gap-2 transition-all`}>
                      See plan <ArrowRight className="w-3 h-3" />
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── Bottom CTA ── */}
        <div className="bg-gradient-to-br from-slate-900 to-primary py-20 text-white text-center">
          <div className="max-w-xl mx-auto px-6">
            <h3 className="text-4xl font-display font-bold mb-4">Ready to get started?</h3>
            <p className="text-white/70 mb-8">
              Free plan. No credit card. Live in 60 seconds.
            </p>
            <Link
              href="/signup"
              className="inline-flex items-center gap-2 bg-white text-slate-900 font-bold px-8 py-4 rounded-xl shadow-xl hover:shadow-2xl hover:-translate-y-0.5 transition-all"
            >
              Create Your Free QR Profile <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
